import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '~/eds/src/components/Button/Button';
import { CopyButton } from '~/eds/src/components/Button/subcomponents/CopyButton';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Info } from '~/eds/src/modules/Info/Info';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';

import { EndpointStatus } from '../types';
import { getUpdatedFilters, testIsActiveStatus } from '../utils';

export const EndpointDetails = () => {
  const intl = useIntl();
  const { setSearchParams } = useRouting();
  const dispatch = useDispatch();
  const { client } = useCurrentUser();

  const filters = useSelector(slices.webhooks.selectors.selectedFilters);
  const { activeEndpoint, activeEndpointType: action } = useSelector(
    slices.webhooks.selectors.selectActiveEndpoint,
  );

  const {
    data: userCreated = {
      firstName: '',
      lastName: '',
      email: '',
    },
    isLoading: userCreatedLoading,
  } = api.endpoints.resolveUser.useQuery(
    {
      id: activeEndpoint?.createdBy ?? 0,
      params: { clientId: client },
    },
    { skip: !activeEndpoint },
  );

  const {
    data: userModified = {
      firstName: '',
      lastName: '',
      email: '',
    },
    isLoading: userModifiedLoading,
  } = api.endpoints.resolveUser.useQuery(
    {
      id: activeEndpoint?.modifiedBy ?? 0,
      params: { clientId: client },
    },
    { skip: !activeEndpoint },
  );

  const [isKeyVisible, _toggle, showKey, hideKey] = useToggle();

  if (!activeEndpoint || action !== 'view') return null;

  return (
    <Panel
      actions={[
        {
          icon: 'x',
          text: intl.formatMessage({
            id: 'EndpointDetails.closeAction',
            defaultMessage: 'Close',
            description: 'Close button text',
          }),
          onClick: () => {
            dispatch(slices.webhooks.actions.resetEndpointConfiguration());
            hideKey();
          },
        },
      ]}
      children={
        <Info
          sections={[
            {
              title: activeEndpoint.endpointUrl,
              infos: [
                {
                  title: intl.formatMessage({
                    id: 'EndpointDetails.statusTitle',
                    defaultMessage: 'Status',
                    description: 'Status field title',
                  }),
                  type: 'text' as const,
                  props: { text: activeEndpoint.status },
                },
                {
                  title: intl.formatMessage({
                    id: 'EndpointDetails.descriptionTitle',
                    defaultMessage: 'Description',
                    description: 'Description field title',
                  }),
                  type: 'text' as const,
                  props: { text: activeEndpoint.description },
                },
                {
                  title: intl.formatMessage({
                    id: 'EndpointDetails.selectedEventsTitle',
                    defaultMessage: 'Selected Events',
                    description: 'Selected events field title',
                  }),
                  type: 'text' as const,
                  props: {
                    text: activeEndpoint.events.join(', ').toString(),
                  },
                },
                {
                  title: intl.formatMessage({
                    id: 'EndpointDetails.createdTitle',
                    defaultMessage: 'Created',
                    description: 'Created field title',
                  }),
                  isLoading: userCreatedLoading,
                  type: 'user-activity' as const,
                  props: {
                    activity: intl.formatMessage({
                      id: 'EndpointDetails.createdActivity',
                      defaultMessage: 'Created',
                      description: 'Created activity label',
                    }),
                    date: activeEndpoint.dateCreated,
                    user: {
                      id: activeEndpoint.createdBy,
                      firstName: userCreated?.firstName,
                      lastName: userCreated?.lastName,
                      email: userCreated?.email,
                    },
                  },
                },
                {
                  title: intl.formatMessage({
                    id: 'EndpointDetails.modifiedTitle',
                    defaultMessage: 'Modified',
                    description: 'Modified field title',
                  }),
                  isLoading: userModifiedLoading,
                  type: 'user-activity' as const,
                  props: {
                    activity: intl.formatMessage({
                      id: 'EndpointDetails.modifiedActivity',
                      defaultMessage: 'Modified',
                      description: 'Modified activity label',
                    }),
                    date: activeEndpoint.dateModified,
                    user: {
                      id: activeEndpoint.modifiedBy,
                      firstName: userModified?.firstName,
                      lastName: userModified?.lastName,
                      email: userModified?.email,
                    },
                  },
                },
                {
                  title: intl.formatMessage({
                    id: 'EndpointDetails.secureSigningTitle',
                    defaultMessage: 'Secure Signing',
                    description: 'Secure signing field title',
                  }),
                  type: 'content' as const,
                  props: {
                    children: (
                      <>
                        {isKeyVisible ? (
                          <Layout direction="column">
                            <Layout preset="buttons">
                              <TruncateText maxW={300}>
                                {activeEndpoint.secret}
                              </TruncateText>
                              <CopyButton
                                copyText={activeEndpoint.secret}
                                disableText
                                size="s"
                              />
                              <IconButton
                                text="rotate"
                                icon="sync"
                                size="s"
                                tooltip={intl.formatMessage({
                                  id: 'EndpointDetails.refreshKeyTooltip',
                                  defaultMessage: 'Refresh key',
                                  description: 'Refresh key button tooltip',
                                })}
                                onClick={() => {
                                  dispatch(
                                    slices.webhooks.actions.setActiveEndpointType(
                                      'rotate-key',
                                    ),
                                  );
                                }}
                              />
                            </Layout>
                            <Layout
                              styles={{
                                '> button': {
                                  padding: 0,
                                },
                              }}
                            >
                              <Button
                                text={intl.formatMessage({
                                  id: 'EndpointDetails.hideButton',
                                  defaultMessage: 'Hide',
                                  description: 'Hide button text',
                                })}
                                onClick={hideKey}
                                variant="action"
                              />
                            </Layout>
                          </Layout>
                        ) : (
                          <Layout
                            styles={{
                              '> button': {
                                padding: 0,
                              },
                            }}
                          >
                            <Button
                              text={intl.formatMessage({
                                id: 'EndpointDetails.revealKeyButton',
                                defaultMessage: 'Reveal Key',
                                description: 'Reveal key button text',
                              })}
                              onClick={showKey}
                              variant="action"
                            />
                          </Layout>
                        )}
                      </>
                    ),
                  },
                },
                {
                  title: '',
                  type: 'link' as const,
                  props: {
                    text: intl.formatMessage({
                      id: 'EndpointDetails.seeAllAttemptsLink',
                      defaultMessage: 'See all attempts',
                      description: 'See all attempts link text',
                    }),
                    onClick: () => {
                      const updatedFilters = getUpdatedFilters({
                        filters,
                        endpoint: activeEndpoint,
                      });
                      dispatch(
                        slices.webhooks.actions.setFilters(updatedFilters),
                      );
                      setSearchParams({ secondaryTab: 'attempts' });
                      dispatch(
                        slices.webhooks.actions.setActiveEndpointType(null),
                      );
                    },
                  },
                },
              ],
            },
          ]}
        />
      }
      footer={{
        actions: [
          {
            text: intl.formatMessage({
              id: 'EndpointDetails.deleteAction',
              defaultMessage: 'Delete',
              description: 'Delete button text',
            }),
            level: 'tertiary',
            onClick: () => {
              dispatch(slices.webhooks.actions.setActiveEndpointType('delete'));
            },
          },
          {
            text: testIsActiveStatus(activeEndpoint.status as EndpointStatus)
              ? intl.formatMessage({
                  id: 'EndpointDetails.suspendAction',
                  defaultMessage: 'Suspend',
                  description: 'Suspend button text',
                })
              : intl.formatMessage({
                  id: 'EndpointDetails.activateAction',
                  defaultMessage: 'Activate',
                  description: 'Activate button text',
                }),
            onClick: () => {
              const action =
                activeEndpoint.status === 'active' ? 'suspend' : 'activate';
              dispatch(slices.webhooks.actions.setActiveEndpointType(action));
            },
          },
          {
            text: intl.formatMessage({
              id: 'EndpointDetails.editAction',
              defaultMessage: 'Edit',
              description: 'Edit button text',
            }),
            onClick: () => {
              dispatch(slices.webhooks.actions.setActiveEndpointType('edit'));
            },
          },
        ],
      }}
      hidden={{
        isHidden: false,
        onHide: () => {
          dispatch(slices.webhooks.actions.resetEndpointConfiguration());
        },
      }}
      position="right"
      title={intl.formatMessage({
        id: 'EndpointDetails.panelTitle',
        defaultMessage: 'Endpoint Configuration',
        description: 'Panel title',
      })}
      width="m"
    />
  );
};
